import { Injectable, Injector } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  CreateFormRules,
  ENTITY_TYPES,
  IBaseFormService,
  IInventoryItemMeasureInVendor,
  IOrderAddPendingVendorFormFieldPath,
  IOrderFormFieldPath,
  ISelectOptionsType,
  IBaseEntityType,
  IOrderAddPendingVendorPopUp,
  EDeployStatus,
  NotificationType,
  IOrderAddPendingInventoryItemVenue,
  IVendorMeasure,
  IInventoryItemVenueVendor,
  IInventoryItemVendor,
  MEASURE_TRANSACTION,
} from '@app/shared/types';
import { EntityServiceProvider } from '@shared/services/datasource';
import { NumberFormatService } from '@shared/services/number-format';
import { CountryFormatService } from '@shared/services/country-format';
import { lazyInject } from '@containers/base/decorators';
import { BaseFormService } from '@shared/services/base';
import { createFormRules as createOrderAddPendingFormRules } from './order-add-pending-form-rules';
import { StateService } from '../state-service';
import { ValidationService } from '@app/components/form/services/validation/validation.service';
import { InventoryItemVenueService } from '@shared/services/inventory-item-venue';
import {
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import { ORDER_FORM_FIELD_PATHS } from '@shared/services/orders/order-form-field-paths';
import {
  chain,
  cloneDeep,
  differenceBy,
  filter,
  find,
  forEach,
  get,
  has,
  head,
  isEmpty,
  isEqual,
  keyBy,
  map,
  merge,
  remove,
  set,
  size,
  toLower,
} from 'lodash';
import { BehaviorSubject, forkJoin, Subject } from 'rxjs';
import { createOrderAddPendingModal } from './order-add-pending-modal.definition';
import { UiActions } from '@app/store/actions';
import { DmDataService } from '../dm-data';
import { CartService } from '../cart';
import {
  generateUnitTextV2,
  getDefaultMeasure,
  getSelectedItemsDescriptionText,
  getStartOfLocalDayISOString,
} from '@app/shared/utils';
import { observableThrowError, rx } from '@app/shared/rx';
import { SelectOption } from '@shared/types';
import { XuxGridAction } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions';
import {
  TOASTER_ERROR_MESSAGE,
  TOASTER_TRANSACTION_FAILED_MESSAGE,
} from '@shared/constants';
import { HttpErrorResponse } from '@angular/common/http';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { GridApi, RowNode } from 'ag-grid-community';
import {
  ENTITY_ID_PATH,
  ORIGINAL_VALUE_FIELD,
  TRANSLATION_OBJECT_FIELD,
} from '@config/global-constants';
import { I18n } from '@app/i18n/strings-keys';
import { mapToI18nSubj } from '@app/shared/utils/user-defined-i18n.utils';

@Injectable()
export class OrderAddPendingFormService
  extends BaseFormService
  implements IBaseFormService<ENTITY_TYPES.ORDER>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.ORDER;

  @lazyInject(EntityServiceProvider)
  public entityServiceProvider: EntityServiceProvider;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(CountryFormatService)
  public countryFormatService: CountryFormatService;
  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(UiActions)
  public uiActions: UiActions;
  @lazyInject(DmDataService)
  public dmDataService: DmDataService;
  @lazyInject(CartService)
  public cartService: CartService;

  public createFormRulesFunction: CreateFormRules<OrderAddPendingFormService> =
    createOrderAddPendingFormRules;
  public measuresForVendor: ISelectOptionsType[] = [];
  public form: FormGroup;
  public formFieldPath: IOrderFormFieldPath = ORDER_FORM_FIELD_PATHS;
  public needSaveToDMItem: IOrderAddPendingInventoryItemVenue = null;
  public addVendorFormFieldPath: IOrderAddPendingVendorFormFieldPath = {
    name: 'name',
    vendor_item_code: 'vendor_item_code',
    measure_entity_id: 'measure_entity_id',
    cost: 'cost',
    site_availability: 'site_availability',
  };

  public majorItems$: BehaviorSubject<SelectOption[]> = new BehaviorSubject([]);
  public minorItems$: BehaviorSubject<SelectOption[]> = new BehaviorSubject([]);
  public gLAItems$: BehaviorSubject<SelectOption[]> = new BehaviorSubject([]);
  public addedItemListDescriptionObs$: BehaviorSubject<string> =
    new BehaviorSubject(
      getSelectedItemsDescriptionText(0, 0, {
        i18nService: this.i18nService,
      }),
    );
  public inlineRemoveAction$: Subject<XuxGridAction> = new Subject();
  public listLoader$: Subject<boolean> = new Subject();
  public saveToDMVendors: IInventoryItemVendor[] = [];
  public listGridApi: GridApi;
  public selectedMeasure = null;
  public constructor(public injector: Injector) {
    super(injector);
  }

  public onFormReady(form: FormGroup): void {
    this.form = form;
    this.listLoader$.next(true);
    this.initItemList();
    this.initDeployStatusSubscriptions();
  }

  public initDeployStatusSubscriptions(): void {
    this.subscriptions.push(
      this.cartService.deployStatus$.subscribe((status: EDeployStatus) => {
        this.uiActions.disableLoader();
        if (isEqual(status, EDeployStatus.deployed)) {
          this.needSaveToDMItem.site_availability = true;
          set(this.needSaveToDMItem, 'vendors', this.saveToDMVendors);
          this.addToOderPending(this.needSaveToDMItem, this.selectedMeasure);
          this.needSaveToDMItem = null;
          this.saveToDMVendors = null;
          this.selectedMeasure = null;
        }
      }),
    );
  }

  public initItemList() {
    forkJoin([
      this.inventoryItemVenueService.fetchList(
        {
          filter: 'is_active eq true',
          select: [
            'entity_id',
            'name',
            'code',
            'reporting_category_major',
            'reporting_category_minor',
            'gl_account',
            'vendors',
            'measures',
            'preferred_vendor',
          ],
        },
        { fetchFullListData: true },
      ),
      this.inventoryItemVenueService.fetchCurrentList(
        { select: ['entity_id'] },
        [get(this, 'document.site_id')],
      ),
    ])
      .pipe(
        rx.catchError((error) => {
          this.listLoader$.next(false);
          setFormFieldValue(
            this.form,
            this.formFieldPath.virtualItemList,
            [],
            false,
          );
          this.uiActions.showToasterMessage({
            type: NotificationType.ERROR,
            title: TOASTER_ERROR_MESSAGE.title,
            body: TOASTER_TRANSACTION_FAILED_MESSAGE,
          });

          return error;
        }),
      )
      .subscribe(
        ([{ items }, { items: currentItems }]: {
          items: IOrderAddPendingInventoryItemVenue;
        }[]) => {
          this.listLoader$.next(false);

          const currentItemMap: {
            [key: string]: IOrderAddPendingInventoryItemVenue;
          } = keyBy(currentItems, 'entity_id');

          const dmItems: IOrderAddPendingInventoryItemVenue[] = map(
            items,
            (item) => ({
              ...item,
              site_availability: has(currentItemMap, item.entity_id),
              item_entity_id: item.entity_id,
            }),
          );

          const inventoryItems: IOrderAddPendingInventoryItemVenue[] =
            getFormFieldValue(
              this.form,
              this.formFieldPath.virtualInventoryItemVenues,
            );

          const itemList: IOrderAddPendingInventoryItemVenue[] = differenceBy(
            dmItems,
            inventoryItems,
            'item_entity_id',
          );

          setFormFieldValue(
            this.form,
            this.formFieldPath.virtualItemList,
            itemList,
            false,
          );
        },
      );
  }

  public redrawRows(
    inventoryItem: IOrderAddPendingInventoryItemVenue,
    node: RowNode,
  ): void {
    inventoryItem.isFocus = false;
    this.listGridApi.redrawRows({
      rowNodes: [node],
    });
  }

  public async showModalDialogHandler(
    inventoryItem: IOrderAddPendingInventoryItemVenue,
    node: RowNode,
  ): Promise<void> {
    this.listGridApi.redrawRows({
      rowNodes: [node],
    });
    this.needSaveToDMItem = inventoryItem;
    const vendor = find(inventoryItem.vendors, {
      entity_id: this.document.vendor_entity_id,
    });
    let measuresForVendor = [];
    let needPrepareVendor: boolean = true;

    if (!isEmpty(get(vendor, 'measures'))) {
      measuresForVendor = get(vendor, 'measures');
      needPrepareVendor = false;
    } else {
      measuresForVendor = filter(
        inventoryItem.measures,
        (measure) => !!measure.transactions.delivery,
      );
      forEach(measuresForVendor, (measure) => {
        if (!has(measure, 'is_default')) {
          set(measure, 'is_default', get(measure, 'default_unit.delivery'));
        }
      });
    }
    this.measuresForVendor = chain(measuresForVendor)
      .sortBy((measure) => toLower(get(measure, 'description')))
      .map((measure) => ({
        text: generateUnitTextV2(measure, {
          measureTransactionType: MEASURE_TRANSACTION.Order,
          numberFormatService: this.numberFormatService,
        }),
        value: measure.entity_id,
      }))
      .valueOf();

    const defaultMeasure = getDefaultMeasure(
      measuresForVendor,
      MEASURE_TRANSACTION.Order,
    );
    const code: string = get(defaultMeasure, 'vendor_item_code', null);
    const cost: number = get(defaultMeasure, 'vendor_cost', null);

    const modalOptions = createOrderAddPendingModal(this, {
      inventoryItem,
      code,
      cost,
      defaultMeasureEntityId: get(defaultMeasure, ENTITY_ID_PATH),
    });
    const modalResult = await this.uiActions.promptFormModal(
      merge(modalOptions, {
        onFormReady: (form: FormGroup): void => {
          form.controls.measure_entity_id.valueChanges
            .pipe(rx.distinctUntilChanged())
            .subscribe((measureEntityId: string) => {
              const measure = find(measuresForVendor, {
                entity_id: measureEntityId,
              });
              setFormFieldValue(
                form,
                this.addVendorFormFieldPath.vendor_item_code,
                get(measure, 'vendor_item_code'),
                false,
              );
              setFormFieldValue(
                form,
                this.addVendorFormFieldPath.cost,
                get(measure, 'vendor_cost'),
                false,
              );
            });
        },
      }),
    );
    this.redrawRows(inventoryItem, node);
    if (!modalResult.confirmed) return;
    this.selectedMeasure = find(measuresForVendor, {
      entity_id: modalResult.value.measure_entity_id,
    });

    if (inventoryItem.site_availability && !isEmpty(get(vendor, 'measures'))) {
      this.addToOderPending(inventoryItem, this.selectedMeasure);
      this.redrawRows(inventoryItem, node);

      return;
    }
    this.uiActions.preventLoaderCloseWhenTimeout(true);
    this.uiActions.enableLoader();
    this.dmDataService
      .bulkUpdateDMEntity(
        ENTITY_TYPES.INVENTORY_ITEM_VENUE,
        this.prepareSaveToDMData(
          inventoryItem,
          modalResult.value,
          this.selectedMeasure,
          needPrepareVendor,
        ),
        get(this.document, ['site_id']),
      )
      .pipe(
        rx.catchError((error: HttpErrorResponse) => {
          this.uiActions.disableLoader();
          this.redrawRows(inventoryItem, node);

          return observableThrowError(error);
        }),
      )
      .subscribe((item: IBaseEntityType) => {
        this.redrawRows(inventoryItem, node);

        this.cartService.intervalFetchCart(get(item, '_id'), 5000, {
          query: {
            select: ['meta'],
          },
        });
      });
  }

  public prepareSaveToDMData(
    inventoryItem: IOrderAddPendingInventoryItemVenue,
    modalValue: IOrderAddPendingVendorPopUp,
    selectedMeasure: IInventoryItemMeasureInVendor,
    needPrepareVendor: boolean = true,
  ): IBaseEntityType[] {
    const newItem = cloneDeep(inventoryItem);
    const docs: IBaseEntityType[] = [];
    let vendors = get(newItem, 'vendors', []);
    if (needPrepareVendor) {
      const masterDoc = {
        _id: get(newItem, '_id'),
        entity_id: get(newItem, 'entity_id'),
        is_master: true,
      };
      vendors = [
        ...vendors,
        {
          name: this.document.vendor_name,
          entity_id: this.document.vendor_entity_id,
          measures: [
            {
              id: get(selectedMeasure, 'id'),
              entity_id: modalValue.measure_entity_id,
              code: get(selectedMeasure, 'code'),
              description: get(selectedMeasure, 'description'),
              factor_value: get(selectedMeasure, 'factor_value'),
              vendor_cost: modalValue.cost,
              vendor_item_code: modalValue.vendor_item_code,
              i18n: selectedMeasure.i18n,
            },
          ],
          i18n: {
            [ORIGINAL_VALUE_FIELD]: {
              name: get(this.document, [
                TRANSLATION_OBJECT_FIELD,
                ORIGINAL_VALUE_FIELD,
                'vendor_name',
              ]),
            },
          },
        },
      ] as IInventoryItemVendor[];
      set(this.selectedMeasure, 'vendor_cost', modalValue.cost);
      if (isEmpty(get(newItem, ['preferred_vendor', 'entity_id']))) {
        set(masterDoc, 'preferred_vendor', {
          entity_id: this.document.vendor_entity_id,
          measure_entity_id: modalValue.measure_entity_id,
        });
      }
      set(masterDoc, 'vendors', vendors);

      docs.push(masterDoc);
    }

    if (!newItem.site_availability) {
      const patch = {
        // use new effective date here
        effective_date: getStartOfLocalDayISOString(),
        entity_id: newItem.entity_id,
        is_master: false,
        contain_patch: true,
        patch: {
          is_active: [
            {
              value: true,
              site_ids: [this.document.site_id],
            },
          ],
        },
        _is_current: [
          {
            value: true,
            site_ids: [this.document.site_id],
          },
        ],
      };
      docs.push(patch);
    }
    this.saveToDMVendors = vendors as IInventoryItemVendor[];

    return docs;
  }

  public getVendorMeasure(
    item: IOrderAddPendingInventoryItemVenue,
  ): IVendorMeasure {
    const measures = this.getVendorMeasures(item);

    return size(measures) === 1 ? head(measures) : null;
  }

  public getVendorMeasures(
    item: IOrderAddPendingInventoryItemVenue,
  ): IVendorMeasure[] {
    if (isEmpty(get(item, 'vendors'))) return [];
    const vendor: IInventoryItemVenueVendor = find(
      item.vendors,
      (v: IInventoryItemVenueVendor) =>
        v.entity_id === get(this, 'document.vendor_entity_id'),
    );

    return get(vendor, 'measures', []);
  }

  public addToOderPending(
    item: IOrderAddPendingInventoryItemVenue,
    vendorMeasure: IVendorMeasure = null,
  ): void {
    const measure: IVendorMeasure =
      vendorMeasure || this.getVendorMeasure(item);
    if (isEmpty(item) || isEmpty(measure)) return;
    const itemList: IOrderAddPendingInventoryItemVenue[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualItemList,
    );
    const addedItemList: IOrderAddPendingInventoryItemVenue[] =
      getFormFieldValue(this.form, this.formFieldPath.virtualAddedItemList);
    remove(itemList, (data) => data.entity_id === item.entity_id);

    const i18nMeasure = mapToI18nSubj(measure, {
      prefix: 'measure' as const,
    });

    addedItemList.push({
      ...item,
      measure_entity_id: i18nMeasure.measure_entity_id,
      measure_description: i18nMeasure.measure_description,
      measure_factor_value: i18nMeasure.measure_factor_value,
      initial_count: null,
      initial_item_cost: measure.vendor_cost,
      initial_total_cost: 0,
      count: null,
      item_cost: `${measure.vendor_cost}`,
      total_cost: 0,
      i18n: merge({}, item.i18n, i18nMeasure.i18n),
    });
    setFormFieldValue(this.form, this.formFieldPath.virtualItemList, itemList);
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualAddedItemList,
      addedItemList,
    );
  }

  /**
   * Removes items that have already been added
   *
   * @param items List of items to remove If the list is empty, no action is required
   */
  public removeFromOderPending(
    items: IOrderAddPendingInventoryItemVenue[],
  ): void {
    if (isEmpty(items)) return;

    const itemList: IOrderAddPendingInventoryItemVenue[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualItemList,
    );
    const addedItemList: IOrderAddPendingInventoryItemVenue[] =
      getFormFieldValue(this.form, this.formFieldPath.virtualAddedItemList);

    itemList.push(...items);

    setFormFieldValue(this.form, this.formFieldPath.virtualItemList, itemList);
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualAddedItemList,
      differenceBy(addedItemList, items, 'item_entity_id'),
    );
  }

  public getAddedItemsRequireValidator(): ValidatorFn {
    const i18nService = this.i18nService;

    return function (control: AbstractControl): ValidationErrors | null {
      const addedItemList: IOrderAddPendingInventoryItemVenue[] = get(
        control,
        'value',
        [],
      );

      const inVainItems: IOrderAddPendingInventoryItemVenue[] = filter(
        addedItemList,
        (item) => isEmpty(item.count) || isEmpty(item.item_cost),
      );

      return isEmpty(inVainItems)
        ? null
        : {
            error: i18nService.instant(
              I18n.ErrorMessages.QuantityReceivedAndReceivedUnitCostRequired,
            ),
          };
    };
  }

  /**
   * will fire this when list grid ready,
   *
   */
  public getGridReadyHandler(): (
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ) => void {
    return (action: XuxGridAction): void => {
      this.listGridApi = action.payload?.gridApi;
    };
  }
}
